import { Injectable } from '@angular/core';
import { interval, merge, Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ErrorHelper from 'src/app/helpers/error-helper';
import { Tour } from 'src/app/model/tour/tour';
import { OfflineStorageTourService } from 'src/app/pages/immo-ip/placer/tour-view/offline-storage/offline-storage-tour.service';
import { ToggleRouteCheckedControllerRequestService } from 'src/app/pages/immo-ip/tour/tour-page/employee-tour-edit-modal/service/toggle-route-checked-controller-request.service';
import { ConnectionStatusService } from '../connection-status.service';
import { ImmoIPTourRouteCheckedChangesObject } from '../immo-ip-placer-offline-changes/immo-ip-tour-route-checked-changes/immo-ip-tour-route-checked-changes-object';
import { ImmoIPTourRouteCheckedChangesService } from '../immo-ip-placer-offline-changes/immo-ip-tour-route-checked-changes/immo-ip-tour-route-checked-changes.service';
import { SnackService } from '../snack.service';
import { ImmoIPPlacerTourRouteCheckedSubmitQueueService } from './immo-ip-placer-tour-route-checked-submit-queue.service';

@Injectable({
  providedIn: 'root'
})
export class ImmoIPPlacerTourRouteCheckedSubmitService {
  private isSubmitting: boolean = false;
  private online: boolean = true;
  private readonly trySubmitInterval = 1000 * 10; // 10 seconds
  private readonly throttleInterval = 200; // 200 ms
  private submitTrigger = new Subject<void>();

  constructor(
    private connectionStatusService: ConnectionStatusService,
    private offlineStorageTourService: OfflineStorageTourService,
    private immoIPTourRouteCheckedChangesService: ImmoIPTourRouteCheckedChangesService,
    private immoIPPlacerTourRouteCheckedSubmitQueueService: ImmoIPPlacerTourRouteCheckedSubmitQueueService,
    private toggleRouteCheckedControllerRequestService: ToggleRouteCheckedControllerRequestService,
    private snackService: SnackService
  ) {
    this.connectionStatusService
      .getConnectionStatusSubject()
      .subscribe((online: boolean) => {
        const doSubmit = !this.online && online;
        this.online = online;
        if (doSubmit) {
          this.submitTrigger.next();
        }
      });
    merge(this.submitTrigger, interval(this.trySubmitInterval)).subscribe(
      () => {
        this.doSubmit();
      }
    );
  }

  private async doSubmit(): Promise<void> {
    if (this.isSubmitting || !this.online) {
      return;
    }
    this.isSubmitting = true;
    try {
      let toSubmit = await this.immoIPPlacerTourRouteCheckedSubmitQueueService.getToSubmit();
      while (CollectionHelper.isPopulated(toSubmit) && this.online) {
        for (const submitEntry of toSubmit) {
          const tourRouteCheckedChangesObject = await this.immoIPTourRouteCheckedChangesService.getObject(
            submitEntry
          );
          if (tourRouteCheckedChangesObject == null) {
            await this.immoIPPlacerTourRouteCheckedSubmitQueueService.removeToSubmit(
              submitEntry
            );
          } else {
            await this.doSubmitTourRouteCheckedChanges(
              tourRouteCheckedChangesObject
            );
          }
        }

        // we don't want to wait 10 seconds if user has submitted tour route checked change while the current queue is being processed
        const reloadToSubmitPromise = new Promise<void>((resolve, reject) => {
          setTimeout(async () => {
            toSubmit = await this.immoIPPlacerTourRouteCheckedSubmitQueueService.getToSubmit();
            resolve();
          }, this.throttleInterval);
        });
        await reloadToSubmitPromise;
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private async doSubmitTourRouteCheckedChanges(
    tourRouteCheckedChangesObject: ImmoIPTourRouteCheckedChangesObject
  ): Promise<void> {
    try {
      await this.toggleRouteCheckedControllerRequestService.setRouteChecked(
        tourRouteCheckedChangesObject.id,
        tourRouteCheckedChangesObject.routeChecked
      );
      await this.offlineStorageTourService.updateTourIfExists(
        tourRouteCheckedChangesObject.dateString,
        (tour: Tour, routeChecked: boolean) => {
          tour.routeChecked = routeChecked;
        },
        tourRouteCheckedChangesObject.routeChecked
      );
      await this.immoIPTourRouteCheckedChangesService.removeObject(
        tourRouteCheckedChangesObject
      );
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err)) {
        this.snackService.showError(err);
        await this.immoIPTourRouteCheckedChangesService.removeObject(
          tourRouteCheckedChangesObject
        );
      }
    }
  }

  public async submitTourRouteChecked(
    tourRouteCheckedChangesObject: ImmoIPTourRouteCheckedChangesObject
  ): Promise<void> {
    const storeChangesPromise = this.immoIPTourRouteCheckedChangesService.storeObject(
      tourRouteCheckedChangesObject
    );
    const storeSubmitQueuePromise = this.immoIPPlacerTourRouteCheckedSubmitQueueService.addToSubmit(
      tourRouteCheckedChangesObject.id
    );
    await Promise.all([storeChangesPromise, storeSubmitQueuePromise]);
    this.submitTrigger.next();
  }
}
